import { Button } from '@helvetia/components/Button'
import Input from '@helvetia/components/forms/Input'
import PhoneInput from '@helvetia/components/forms/PhoneInput'
import { Col, Row } from '@helvetia/components/layout'
import LinkText from '@helvetia/components/LinkText'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import AgentService from '../../../core/services/agent.service'
import { CheckboxField } from '../../_components/CheckboxField'
import { LifecycleView } from '../../_components/ConnectComponent'
import { goToPrivacyPolicyLink } from '../../_utils/links'
import { CalculatorStepper } from '../CaculatorStepper'
import { ContactDataPresenter } from './ContactData.presenter'
import { validateFieldForm } from './ContactData.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface ContactDataInfoValues {
  company: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  acceptedPrivacyPolicy: boolean
}

interface Props {
  presenter: ContactDataPresenter
  onSubmit: (values: ContactDataInfoValues) => void
}

interface State {
  initialValues?: ContactDataInfoValues
  isEditing: boolean
  isLoading: boolean
  acceptedPrivacyPolicy: boolean
}

export class ContactDataSection
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: ContactDataPresenter

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.state = {
      initialValues: null,
      isEditing: false,
      isLoading: false,
      acceptedPrivacyPolicy: false,
    }
  }

  setInitialValues = (initialValues, isEditing: boolean) => {
    this.setState({
      initialValues,
      isEditing,
      acceptedPrivacyPolicy: initialValues.acceptedPrivacyPolicy,
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickContinuar = (values: ContactDataInfoValues) =>
    this.presenter.onContinue(values)

  handleClickBack = () => {
    this.presenter.onBackClick()
  }

  render() {
    const { t } = this.props
    const { isEditing } = this.state

    if (!this.state.initialValues) return <Spinner size="xl" />
    return (
      <>
        <Row>
          <Col tablet={12} desktop={4} verticalGutter>
            <CalculatorStepper step={6} />
          </Col>

          <Col tablet={12} desktop={8}>
            <Row marginTop="xxs">
              <Text as="p" color="black" size="l">
                {t('step6.title')}
              </Text>
            </Row>

            <Formik
              initialValues={this.state.initialValues}
              onSubmit={this.handleClickContinuar}
              validate={values => validateFieldForm(values)}
              render={({ setFieldValue, values }) => (
                <Form>
                  <Row marginTop="xs" marginBottom="s">
                    <Col>
                      <Field
                        component={Input}
                        name="company"
                        text={
                          AgentService.isAgentOrBroker()
                            ? t('contactData.company.agentQuestion')
                            : t('contactData.company.question')
                        }
                        label={t('contactData.company.label')}
                        required
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="firstName"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.firstName.agentQuestion')
                                : t('contactData.firstName.question')
                            }
                            label={t('contactData.firstName.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col mobile={12}>
                          <Field
                            component={Input}
                            name="lastName"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.lastName.agentQuestion')
                                : t('contactData.lastName.question')
                            }
                            label={t('contactData.lastName.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="email"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.email.agentQuestion')
                                : t('contactData.email.question')
                            }
                            label={t('contactData.email.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col mobile={12}>
                          <Field
                            component={PhoneInput}
                            name="phone"
                            text={
                              AgentService.isAgentOrBroker()
                                ? `${t('contactData.phone.agentQuestion')}${t(
                                    'contactData.phone.optional',
                                  )}`
                                : `${t('contactData.phone.question')}${t(
                                    'contactData.phone.optional',
                                  )}`
                            }
                            label={t('contactData.phone.label')}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  {!AgentService.isAgentOrBroker() && (
                    <>
                      <Row marginTop="xxs" marginBottom="xxxs">
                        <Text as="p" color="black" size="m">
                          {t('contactData.privacyPolicy.header')}
                        </Text>
                      </Row>
                      <label>
                        <Field
                          component={CheckboxField}
                          name="acceptedPrivacyPolicy"
                          value={values.acceptedPrivacyPolicy}
                          defaultChecked={values.acceptedPrivacyPolicy}
                          label={
                            <Text as="div" size="s">
                              <Text as="span" size="s">
                                {t('contactData.privacyPolicy.description')}
                              </Text>
                              <LinkText
                                onClick={goToPrivacyPolicyLink}
                                text={t('contactData.privacyPolicy.link')}
                                color="primary"
                              />
                            </Text>
                          }
                          onSelection={() => {
                            setFieldValue(
                              'acceptedPrivacyPolicy',
                              !values.acceptedPrivacyPolicy,
                            )

                            this.setState({
                              acceptedPrivacyPolicy: !this.state
                                .acceptedPrivacyPolicy,
                            })
                          }}
                        />
                      </label>
                    </>
                  )}

                  <Row verticalGutter marginTop="xs" marginBottom="m">
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button
                        primary
                        type="submit"
                        disabled={
                          !AgentService.isAgentOrBroker() &&
                          !Boolean(this.state.acceptedPrivacyPolicy)
                        }
                      >
                        {isEditing ? t('buttons.save') : t('buttons.continue')}
                      </Button>
                    </Col>
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button grey onClick={this.handleClickBack}>
                        {isEditing ? t('buttons.cancel') : t('buttons.back')}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const ContactDataView = withNamespaces()(withRouter(ContactDataSection))
